import {
  IconMessageCircle,
  IconHeadphones,
  IconHeadphonesOff,
  IconArrowUpRight,
} from '@tabler/icons-react'
import queryString from 'query-string'
import { isNil } from 'lodash'

import {
  startListeningToAgentAlert,
  stopListeningToAlert,
} from '@/reducers/realtime/realtime.actions'
import { stopListeningToCall } from '@/reducers/commandCenter/commandCenter.redux'
import { BEGIN_CHAT, OPEN_ASSIST_PANEL } from '@/reducers/realtime/realtimeChats.redux'

// The live-call context for a notification's Listen/Chat: the agent's active
// RTC alert, but only when it's for the SAME call this notification is about.
//
// `rtcAlert` (state.realtimeUserAlerts.alerts[agent]) is keyed by agent and
// holds whatever call is live right now. If we used it directly, every past
// coaching row for an agent (raised_hand OR a fired RTA) would light up — and
// point at the agent's CURRENT call — the moment a new call starts. So we tie
// the buttons to the call the notification was about (metadata.call_id) and
// only treat the alert as live for it when the alert's callId matches. When the
// call ends the alert clears, so the buttons go away.
//
// `requireCallId`: both raised_hand and RTA carry metadata.call_id on their WS
// push, so callers pass requireCallId true — we require a match, and if it's
// absent (e.g. after a reload, when the WS-only metadata is gone) we show
// nothing rather than risk pointing an old row at a new call. Returns null when
// there's no usable live context.
export const liveAlertForNotification = (
  rtcAlert,
  notification,
  { requireCallId = false } = {}
) => {
  if (!rtcAlert) {
    return null
  }
  // Prefer the persisted top-level callId (Pass 2) — it survives a reload,
  // unlike the WS-only metadata.call_id — falling back to metadata for
  // in-flight rows created before the column was populated.
  const notificationCallId = notification?.callId ?? notification?.metadata?.call_id
  if (isNil(notificationCallId)) {
    return requireCallId ? null : rtcAlert
  }
  return String(rtcAlert.callId) === String(notificationCallId) ? rtcAlert : null
}

// Per-notification-type action buttons (Image 1). Each entry yields zero,
// one, or two buttons rendered under the notification body. The config
// is FE-only — the backend doesn't need to know about the buttons; it
// just makes sure each notification carries enough context (refId,
// refTable, actionUrl) for the click handlers below to do their thing.
//
// onClick context shape:
//   {
//     notification,             // the bell row, camelCase keys
//     history,                  // react-router useHistory()
//     dispatch,                 // redux dispatch
//     rtcAlert,                 // the live-call context — the agent's active RTC
//                               //   alert, matched to this notification's call
//                               //   (liveAlertForNotification). null when there's
//                               //   no live, matching call.
//     currentUser,              // state.currentUser
//   }
//
// `available({ notification, rtcAlert, agentAssistAvailable, liveListenAvailable })`
// is the same shape, used to gate whether the button renders. raised_hand's
// Listen + Chat need the live-call context (call_id, processing_url, agent_id,
// etc.) the alert carries; without a matching live alert the agent isn't on this
// call and there's nothing to listen to / chat about. Each also needs its own
// surface to exist — see canChat / canListen below.

const linkButton = (label) => ({
  label,
  kind: 'link',
  icon: IconArrowUpRight,
  available: ({ notification }) => Boolean(notification.actionUrl),
  onClick: ({ notification, history }) => {
    if (!notification.actionUrl) {
      return
    }
    history.push(notification.actionUrl)
  },
})

// raised_hand carries the agent's username in refId/refTable. We render Listen
// / Chat only when `rtcAlert` here is the live-call context for that agent (the
// caller passes the call-matched alert from liveAlertForNotification). Without
// it there's nothing to listen to / chat about.
//
const hasLiveCallContext = ({ notification, rtcAlert }) =>
  Boolean(notification.refId) && notification.refTable === 'agent_username' && Boolean(rtcAlert)

// The two surfaces this row can open are gated separately, because they are not
// the same kind of thing.
//
// Chat is manager-to-agent text: available wherever the Agent Assist widget can
// mount, which is everyone the product reaches. Gating it on anything narrower
// is what left a notified manager with a Chat button wired to a panel that
// could never mount (QCER-1103).
//
// Listen is live, unredacted call audio, so it follows `hasLiveListenAccess`
// and nothing may short-circuit past that flag — an org that asked us to turn
// live listen off must not get it back through the bell.
const canChat = (context) => Boolean(context.agentAssistAvailable) && hasLiveCallContext(context)

const canListen = (context) => Boolean(context.liveListenAvailable) && hasLiveCallContext(context)

// The RTA alert types. These are the ones QCER-989 floats as a top-right toast
// and gives a "View call" fallback (below) once the call has ended. Kept as an
// explicit list — there's no rta_ prefix convention to rely on.
export const RTA_ALERT_TYPES = ['rta_alert_positive', 'rta_alert_negative', 'rta_alert_informative']

// The notification types whose bell row offers live coaching (Listen/Chat).
// Single source for NOTIFICATION_ACTIONS below and the panel's
// sticky-while-live behavior. NOT for the per-agent "set up an alert"
// guidance — that stays raised_hand-only by design (NotificationItem):
// RTA recipients cover the agent by definition, and call_started recipients
// are chosen by notification subscription, not alert-config coverage.
export const LIVE_COACHING_TYPES = ['raised_hand', ...RTA_ALERT_TYPES, 'call_started']

// Open the chat for an agent's live alert. The chat (ChatPanel) is now the
// single live-coaching surface — Listen lives inside it — so both the Listen
// and Chat actions open it.
//
// rtcAlert only carries the full display names (agentName / managerName) —
// there are no discrete first/last fields on the alert. Splitting on the first
// space mirrors UserAlertBlock.js exactly, so a chat opened from the bell
// yields the same chat record as one opened from the RTC sidebar. Mononyms and
// multi-word first names inherit that shared limitation; the fix belongs
// upstream on the alert payload, not in two divergent split sites.
const beginChatFromAlert = (rtcAlert, dispatch) => {
  const agentName = rtcAlert.agentName || ''
  const managerName = rtcAlert.managerName || ''
  const [agentFirstName, ...agentLastParts] = agentName.split(' ')
  const [managerFirstName, ...managerLastParts] = managerName.split(' ')
  dispatch({
    type: BEGIN_CHAT,
    alertId: rtcAlert.alertId,
    // Carry the live call_id so a bell-opened chat sends with the same call
    // context as one opened from Live Listen's row (its
    // SEND_CHAT_TO_DESKTOP would otherwise carry call_id=null).
    callId: rtcAlert.callId,
    agentId: rtcAlert.agentId,
    managerId: rtcAlert.managerId,
    agentUsername: rtcAlert.agentUsername,
    managerUsername: rtcAlert.managerUsername,
    agentFirstName: agentFirstName || '',
    agentLastName: agentLastParts.join(' '),
    managerFirstName: managerFirstName || '',
    managerLastName: managerLastParts.join(' '),
  })
  // The bell is the entry point to the Agent Assist workspace, so opening a
  // chat from it pops the panel (BEGIN_CHAT alone doesn't, so CommandCenter's
  // inline chat can seed a record without the panel appearing).
  dispatch({ type: OPEN_ASSIST_PANEL })
}

const liveCallActions = [
  {
    label: 'Start listening',
    kind: 'inline',
    icon: IconHeadphones,
    available: (context) =>
      canListen(context) && context.listeningToUsername !== context.notification.refId,
    onClick: ({ rtcAlert, currentUser, dispatch }) => {
      // Open the chat (the unified live-coaching panel) AND start the listen,
      // so the Listen/Stop control has its home in the chat header.
      beginChatFromAlert(rtcAlert, dispatch)
      dispatch(
        startListeningToAgentAlert({
          alertCallId: rtcAlert.callId,
          agentUsername: rtcAlert.agentUsername,
          processingUrl: rtcAlert.processingUrl,
          managerUsername: currentUser.username,
          alertId: rtcAlert.alertId,
        })
      )
    },
  },
  // While this agent is the one being live-listened to, the row's Listen
  // swaps to Stop. The row can be the manager's only visible control (the
  // assist panel may be minimized), so unlike Listen it must not require
  // the live-call context to render.
  {
    label: 'Stop listening',
    kind: 'inline',
    icon: IconHeadphonesOff,
    available: ({ notification, listeningToUsername, liveListenAvailable }) =>
      Boolean(liveListenAvailable) &&
      Boolean(notification.refId) &&
      notification.refTable === 'agent_username' &&
      listeningToUsername === notification.refId,
    onClick: ({ dispatch }) => {
      // Mirrors ChatPanel's stopListening — clear both listen surfaces.
      dispatch(stopListeningToAlert())
      dispatch(stopListeningToCall())
    },
  },
  {
    label: 'Chats',
    kind: 'inline',
    icon: IconMessageCircle,
    available: canChat,
    onClick: ({ rtcAlert, dispatch }) => {
      beginChatFromAlert(rtcAlert, dispatch)
    },
  },
]

// QCER-989: once an RTA call has ended, the live Listen/Chat context (rtcAlert)
// is gone, so the row/toast would have no action at all. Fall back to a link to
// the flagged call in Call Explorer — the backend points action_url at the alert
// moment (/calls/{id}?timestamp=). Shown only when the call is KNOWN to have
// ended: `!rtcAlert` alone is also true while a cold-join resolve is still in
// flight, so we additionally require `!resolvePending` — otherwise a manager who
// reloaded mid-call would briefly see "View call" (landing on Call Explorer's
// "call in progress" screen) before Listen/Chat appear. This preserves the
// QCER-862 invariant: not-live ⇒ no stale action.
const viewCallAfterEnd = {
  ...linkButton('View call'),
  // Requires callId: without it there's no call to link to (action_url falls back
  // to /live-listen) AND no way to cold-resolve liveness, so we can't tell an
  // ended call from a live one — safer to show nothing than a misleading link.
  available: ({ notification, rtcAlert, resolvePending }) =>
    Boolean(notification.actionUrl) && Boolean(notification.callId) && !rtcAlert && !resolvePending,
}

// RTA rows get the live Listen/Chat actions AND the post-call "View call" link;
// their `available` gates make them mutually exclusive on live-vs-ended.
const rtaActions = [...liveCallActions, viewCallAfterEnd]

export const NOTIFICATION_ACTIONS = {
  // raised_hand gets the live actions only (its action_url is the raised-hand
  // sentinel, not a call link). call_started + RTA types add the post-call
  // "View call" fallback: their action_url is a Call Explorer /calls/{id} link,
  // so once the call ends and the live Listen/Chat context is gone the row
  // swaps to a link to the call (opened at the start for call_started).
  raised_hand: liveCallActions,
  call_started: rtaActions,
  ...Object.fromEntries(RTA_ALERT_TYPES.map((type) => [type, rtaActions])),
  agent_requests_coaching: [{ ...linkButton('Coaching Task') }],
  new_coaching_inbox_item: [{ ...linkButton('Coaching Task') }],
  dispute: [
    {
      label: 'Review Dispute',
      kind: 'link',
      icon: IconArrowUpRight,
      // Deep-link to the dispute's task in the Quality Inbox when the producer
      // tags the notification with its task ref (refTable === 'task', refId ===
      // the workflow task id the inbox reads from ?id=). Fall back to actionUrl
      // for notifications that only carry the scorecard link, so the button
      // never disappears in the window before the producer starts sending the
      // task ref.
      available: ({ notification }) =>
        (notification.refTable === 'task' && Boolean(notification.refId)) ||
        Boolean(notification.actionUrl),
      onClick: ({ notification, history }) => {
        if (notification.refTable === 'task' && notification.refId) {
          history.push({
            pathname: '/qa-copilot/inbox',
            search: queryString.stringify({ id: notification.refId }),
          })
          return
        }
        if (notification.actionUrl) {
          history.push(notification.actionUrl)
        }
      },
    },
  ],
  quality_inbox: [{ ...linkButton('Review Scorecard') }],
  failed_criteria: [{ ...linkButton('Review Scorecard') }],
  failed_scorecard: [{ ...linkButton('Review Scorecard') }],
  new_compliance_inbox_item: [{ ...linkButton('Review Compliance Item') }],
  audio_issue: [{ ...linkButton('View call') }],
  note_mention: [{ ...linkButton('View note') }],
  agent_gpt_no_answer: [{ ...linkButton('View in AI Answers') }],
  // One type for both the start and the finish of an export: they point at
  // the same job page, which already says which state it is in, so a second
  // key here would only duplicate the button -- and would cost a second row
  // in notification settings for one feature.
  call_asset_export: [{ ...linkButton('View export') }],
  kb_scan_ready: [{ ...linkButton('Review files') }],
}

// Public helper — given a notification, the live RTC alert state, and the
// agent username currently being live-listened to (null when not listening),
// return the array of buttons to render. Filters out ones whose `available`
// says no.
// `resolvePending` is true while a cold-join resolve is still in flight — the
// live context isn't known yet, so live-vs-ended action gates (e.g. RTA's "View
// call") can hold off until it settles instead of flashing a stale action.
// `agentAssistAvailable` and `liveListenAvailable` say whether each surface
// exists for this user. Both default to false so a caller that forgets one
// renders no action instead of a dead one.
export const getNotificationActions = (
  notification,
  rtcAlert,
  listeningToUsername = null,
  { resolvePending = false, agentAssistAvailable = false, liveListenAvailable = false } = {}
) => {
  const buttons = NOTIFICATION_ACTIONS[notification.notificationType] || []
  return buttons.filter((button) =>
    button.available
      ? button.available({
          notification,
          rtcAlert,
          listeningToUsername,
          resolvePending,
          agentAssistAvailable,
          liveListenAvailable,
        })
      : true
  )
}
